import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import { fieldAlias, headerLabel } from './LiveDatasetTable';
import { hasUnnestDatasetColumn } from '../../utils/spatialZoneOutputMeta';

export const DEFAULT_SPATIAL_ZONE_CONFIG = {
  enabled: true,
  chartSource: 'zones',
  xColumn: '',
  yColumn: '',
  radiusPx: 25,
  mergeMode: 'count',
  weightColumn: '',
  timeColumn: '',
  timeBucket: null,
  columnLabels: {},
  resultOps: {
    filters: [],
    sortField: undefined,
    sortDir: 'desc',
    limit: undefined,
  },
};

const MERGE_OPTIONS = [
  { value: 'count', label: 'Contar eventos' },
  { value: 'sum', label: 'Sumar peso' },
  { value: 'avg', label: 'Promedio de peso' },
];

const TIME_BUCKET_OPTIONS = [
  { value: '', label: 'Todo el periodo filtrado' },
  { value: 'minute', label: 'Por minuto' },
  { value: 'hour', label: 'Por hora' },
  { value: 'day', label: 'Por día' },
  { value: 'week', label: 'Por semana' },
  { value: 'month', label: 'Por mes' },
];

export default function SpatialZoneAnalysisDialog({
  open,
  onClose,
  value,
  onChange,
  columns = [],
  context,
}) {
  const [cfg, setCfg] = useState(value || DEFAULT_SPATIAL_ZONE_CONFIG);

  useEffect(() => {
    if (!open) return;
    setCfg(value || DEFAULT_SPATIAL_ZONE_CONFIG);
  }, [open, value]);

  const numericOptions = useMemo(() => {
    const opts = [];
    (columns || []).forEach((c, i) => {
      if (!c?.tableId || !c?.column) return;
      if (c.jsonMode === 'arrayUnnest' || c.jsonMode === 'array_unnest') return;
      const alias = fieldAlias(c, `col_${i}`);
      opts.push({ value: alias, label: headerLabel(c, context) || alias });
    });
    return opts;
  }, [columns, context]);

  const datetimeOptions = useMemo(() => {
    const opts = [];
    (columns || []).forEach((c, i) => {
      if (!c?.tableId || !c?.column) return;
      if (c.jsonMode === 'arrayUnnest' || c.jsonMode === 'array_unnest') return;
      const alias = fieldAlias(c, `col_${i}`);
      opts.push({ value: alias, label: headerLabel(c, context) || alias });
    });
    return opts;
  }, [columns, context]);

  const set = useCallback((patch) => setCfg((prev) => ({ ...prev, ...patch })), []);

  const canEnable = hasUnnestDatasetColumn(columns);

  const handleSave = () => {
    onChange(cfg.enabled ? cfg : null);
    onClose();
  };

  const handleDisable = () => {
    onChange(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <GridOnIcon color="primary" fontSize="small" />
        Zonas espaciales
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch checked={cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })} />}
          label="Activar"
          sx={{ mr: 0 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {!canEnable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Agrega una columna JSON en modo <b>unnest</b> (detecciones con x/y) para usar este análisis.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Graficar desde</InputLabel>
            <Select
              label="Graficar desde"
              value={cfg.chartSource || 'zones'}
              onChange={(e) => set({ chartSource: e.target.value })}
              disabled={!cfg.enabled}
            >
              <MenuItem value="zones">Zonas agregadas (ranking, tabla, heatmap)</MenuItem>
              <MenuItem value="original">Serie original (puntos sin agrupar)</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            Agrupa detecciones cercanas en píxeles dentro del radio configurado. Cada fila resultante
            representa una zona con centroide y cantidad de eventos.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="number"
              label="Radio (px)"
              value={cfg.radiusPx ?? 25}
              onChange={(e) => set({ radiusPx: e.target.value })}
              inputProps={{ min: 1, max: 500 }}
              disabled={!cfg.enabled}
              sx={{ width: 120 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Agregación</InputLabel>
              <Select
                label="Agregación"
                value={cfg.mergeMode || 'count'}
                onChange={(e) => set({ mergeMode: e.target.value })}
                disabled={!cfg.enabled}
              >
                {MERGE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Columna de peso (opcional)</InputLabel>
            <Select
              label="Columna de peso (opcional)"
              value={cfg.weightColumn || ''}
              onChange={(e) => set({ weightColumn: e.target.value || '' })}
              disabled={!cfg.enabled}
            >
              <MenuItem value="">Ninguna (peso = 1)</MenuItem>
              {numericOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Partición temporal</InputLabel>
            <Select
              label="Partición temporal"
              value={cfg.timeBucket || ''}
              onChange={(e) => set({ timeBucket: e.target.value || null })}
              disabled={!cfg.enabled}
            >
              {TIME_BUCKET_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {cfg.timeBucket && (
            <FormControl fullWidth size="small">
              <InputLabel>Columna de tiempo</InputLabel>
              <Select
                label="Columna de tiempo"
                value={cfg.timeColumn || ''}
                onChange={(e) => set({ timeColumn: e.target.value || '' })}
                disabled={!cfg.enabled}
              >
                <MenuItem value="">Auto (primera columna fecha)</MenuItem>
                {datetimeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {value?.enabled && (
          <Button color="error" onClick={handleDisable} sx={{ mr: 'auto' }}>
            Quitar análisis
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={cfg.enabled && !canEnable}>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
